import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserPlus, Phone, PhoneCall, PhoneMissed, Star, Repeat, GraduationCap, Trophy, Ban, RefreshCw, ChevronRight } from 'lucide-react';

const card = (key, label, value, Icon, tint) => ({ key, label, value, Icon, tint });

export default function MyDailyReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('en-CA');
  const [period, setPeriod] = useState('day');
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const range = useCallback(() => {
    const d = new Date(date + 'T00:00:00');
    if (period === 'day') return { start: date, end: date };
    if (period === 'week') {
      const s = new Date(d); s.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return { start: s.toLocaleDateString('en-CA'), end: e.toLocaleDateString('en-CA') };
    }
    const s = new Date(d.getFullYear(), d.getMonth(), 1);
    const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start: s.toLocaleDateString('en-CA'), end: e.toLocaleDateString('en-CA') };
  }, [period, date]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true); setError(false);
    try {
      const { start, end } = range();
      const qs = period === 'day' ? `date=${start}` : `start=${start}&end=${end}`;
      const r = await apiClient.get(`/activity-logs/counselor-daily-summary/${user.id}?${qs}`);
      setData(r.data);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [user?.id, range, period]);
  useEffect(() => { load(); }, [load]);

  const rangeLabel = () => {
    const { start, end } = range();
    const fmt = s => new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return period === 'day' ? (date === today ? 'Today' : fmt(start)) : `${fmt(start)} – ${fmt(end)}`;
  };

  const openDrill = async (c) => {
    setDrill(c); setDrillData(null); setDrillLoading(true);
    try {
      const { start, end } = range();
      const q = period === 'day' ? `date=${start}` : `start=${start}&end=${end}`;
      const r = await apiClient.get(`/activity-logs/daily-drilldown?metric=${c.key}&${q}`);
      setDrillData(r.data);
    } catch { setDrillData({ leads: [], count: 0 }); }
    finally { setDrillLoading(false); }
  };

  const c = data?.calls || {};
  const firstCalls = (c.newly_assigned_connected || 0) + (c.newly_assigned_rnr || 0);
  const followUps = (c.followup_connected || 0) + (c.followup_rnr || 0) + (c.rnr_followup_connected || 0) + (c.rnr_followup_rnr || 0);
  const totalCalls = (c.total_connected || 0) + (c.total_not_connected || 0);
  const isCounsellor = ['counselor', 'team_leader'].includes(user?.role);

  const cards = [
    card('assigned', 'New leads', data?.leads_assigned?.total || 0, UserPlus, 'bg-indigo-50 text-indigo-600 border-indigo-200'),
    card('calls', 'Total calls', totalCalls, Phone, 'bg-blue-50 text-blue-600 border-blue-200'),
    card('connected', 'Connected', c.total_connected || 0, PhoneCall, 'bg-green-50 text-green-600 border-green-200'),
    card('rnr', 'RNR (not connected)', c.total_not_connected || 0, PhoneMissed, 'bg-red-50 text-red-600 border-red-200'),
    card('first', 'First calls', firstCalls, Star, 'bg-sky-50 text-sky-600 border-sky-200'),
    card('followups', 'Follow-ups', followUps, Repeat, 'bg-violet-50 text-violet-600 border-violet-200'),
    ...(isCounsellor ? [card('counselling', 'Counselling sessions', data?.counselling_sessions_today || 0, GraduationCap, 'bg-teal-50 text-teal-600 border-teal-200')] : []),
    card('conversions', 'Conversions', data?.conversions_today || 0, Trophy, 'bg-emerald-50 text-emerald-600 border-emerald-200'),
    card('bad', 'Bad leads', data?.bad_leads_today || 0, Ban, 'bg-orange-50 text-orange-600 border-orange-200'),
  ];

  return (
    <div className="space-y-4" data-testid="my-daily-report">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-[#1B2559]" data-testid="daily-report-title">My workflow</h2>
          <p className="text-xs text-slate-500">{rangeLabel()} · new leads created after 6pm count toward the next working day</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-slate-100 p-0.5" data-testid="daily-report-period">
            {[['day', 'Day'], ['week', 'Week'], ['month', 'Month']].map(([key, label]) => (
              <button key={key} onClick={() => setPeriod(key)} className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${period === key ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`} data-testid={`period-${key}`}>{label}</button>
            ))}
          </div>
          <input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} className="h-10 flex-1 sm:flex-initial rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="daily-report-date" />
          <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={load} disabled={loading} aria-label="Refresh" data-testid="daily-report-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      {loading ? <p className="py-8 text-center text-sm text-slate-500" data-testid="daily-report-loading">Loading your numbers…</p>
        : error ? <div className="py-8 text-center" data-testid="daily-report-error"><p className="mb-2 text-sm text-slate-500">Could not load your report.</p><Button variant="outline" size="sm" onClick={load}>Retry</Button></div>
        : <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4" data-testid="daily-report-cards">
            {cards.map((cd) => {
              const { key, label, value, Icon, tint } = cd;
              return (
              <button key={key} onClick={() => openDrill(cd)} className={`text-left rounded-xl border p-3 transition-transform hover:scale-[1.02] active:scale-95 ${tint}`} data-testid={`daily-card-${key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold tabular-nums" data-testid={`daily-value-${key}`}>{value}</span>
                  <Icon className="h-5 w-5 opacity-70" />
                </div>
                <p className="mt-1 flex items-center gap-0.5 text-[11px] font-medium text-slate-600 leading-tight">{label}<ChevronRight className="w-3 h-3 opacity-50" /></p>
              </button>
            ); })}
          </div>}

      <Dialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="my-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{drill?.label} · {rangeLabel()}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto -mx-2 px-2">
            {drillLoading ? <p className="py-8 text-center text-sm text-slate-500" data-testid="my-drilldown-loading">Loading leads…</p>
              : !drillData?.leads?.length ? <p className="py-8 text-center text-sm text-slate-500" data-testid="my-drilldown-empty">No leads for this tile.</p>
              : <ul className="divide-y divide-slate-100" data-testid="my-drilldown-list">
                  {drillData.leads.map((l, i) => (
                    <li key={l.id + i} className="flex items-center gap-2 py-2.5" data-testid={`my-drilldown-row-${l.id}`}>
                      <button onClick={() => { setDrill(null); navigate(`/students/${l.id}`); }} className="min-w-0 flex-1 text-left" data-testid={`my-drilldown-open-${l.id}`}>
                        <p className="truncate text-sm font-semibold text-[#1B2559]">{l.name}</p>
                        <p className="truncate text-[11px] text-slate-500">
                          {l.outcome && <span className="mr-1">{l.outcome}</span>}
                          {l.status && <span className="rounded bg-slate-100 px-1 py-0.5">{l.status.replace(/_/g, ' ')}</span>}
                        </p>
                      </button>
                      {l.phone && <a href={`tel:${l.phone}`} onClick={e => e.stopPropagation()} className="shrink-0 rounded-full bg-green-100 p-2 text-green-700 hover:bg-green-200" aria-label={`Call ${l.name}`} data-testid={`my-drilldown-call-${l.id}`}><PhoneCall className="w-4 h-4" /></a>}
                    </li>
                  ))}
                </ul>}
          </div>
          <p className="pt-1 text-center text-[11px] text-slate-400">{drillData?.count || 0} lead(s) · tap a name to open the profile, or the green button to call</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
